/* eslint-disable react/display-name */
import { FC, memo, useState } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {
    Avatar,
    Box,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Divider,
    Grid,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { blue } from '@mui/material/colors';
import { SCUIButton } from '../../../../atomic/atoms';
import {
    CachedOutlined,
    Circle,
    CloudDownload,
    ConfirmationNumber,
    ExpandMore,
    HealingOutlined,
    LocalAtm,
    SwapVert,
    Task,
    Tune,
    Web,
    Widgets,
} from '@mui/icons-material';
import {
    CampaignStep,
    CampaignRepeatDays,
    CampaignRepeatTypes,
    CampaignStatus,
    CouponExecutionType,
    CouponGenerationType,
    MatrixCampaignTypes,
    RuleSwitchValue,
    RuleType,
    CampaignStatuses,
    CouponGenerationTypes,
    CampaignRepeatType,
    RuleExecutionType,
} from '../../../../types/enums/campaign-enums';
import { ExpandMoreComponent } from '../SCUIExpandMore/SCUIExpandMore';
import moment from 'moment';
import '../../CampaignManagement.scss';
import { sampleTshirt, emptyCampaign, excelLogo } from '../../../../assets/images';
import { ICampaignMatrix, ICouponItem, ImageButtonListProps } from '../../types/interfaces/CampaingTypes';
import {
    StepEarningAndRuleServiceComponent,
    StepWebsiteAppearenceServiceComponent,
    StepCouponServiceComponent,
    StepStackableServiceComponent,
    StepSummaryServiceComponent,
} from '../../services';
import { ButtonWithConfirmation } from '../SCUIButtonWithConfirmation/SCUIButtonWithConfirmation';
import { SCUILogs } from '../SCUILogs/SCUILogs';

const stepCardSx = { display: 'flex', alignItems: 'center' } as const;
const stepCardHeaderSx = { width: '100%', padding: 0 } as const;
const stepCardContentSx = {
    padding: '12px 0',
    '& .MuiTypography-subtitle2': {
        marginRight: '4px',
    },
} as const;
const stepCardContentDividerSx = { width: '100%' } as const;
const stepCardContentBoxSx = { display: 'flex' } as const;
const stepCardContentBoxTitleSx = { fontWeight: 'bold' } as const;

const stepPriorityHeaderItemSx = {
    border: '1px solid',
    display: 'flex',
    flexDirection: 'row',
    margin: '8px 0',
    padding: '8px',
    backgroundColor: 'whitesmoke',
} as const;
const stepPriorityItemSx = {
    border: '1px solid',
    display: 'flex',
    flexDirection: 'row',
    margin: '8px 0',
    padding: '8px',
} as const;
const stepPriorityCurrentItemSx = {
    backgroundColor: 'aliceblue',
    border: '1px solid #2962ff',
    display: 'flex',
    flexDirection: 'row',
    margin: '8px 0',
    padding: '8px',
} as const;

const stackableItemTitleSx = {
    marginBottom: '10px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    fontSize: { xs: '16px', md: '20px', lg: '24px' },
} as const;
const stackableItemDescriptionSx = {
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
} as const;

const dividerSx = { width: '100%', margin: '16px 0' } as const;
const matrixCampaignRowSx = { 'td, th': { border: 0, height: '30px' } } as const;

const getCampaignStatusMeta = (statusId: ICampaignMatrix['status']) =>
    CampaignStatuses.find((s: { id: number }) => s.id == statusId);

const MatrixCampaignStatusChip: FC<{ status: number }> = ({ status }) => {
    const { t } = useTranslation();
    const statusMeta = getCampaignStatusMeta(status);

    return (
        <Chip
            label={t(statusMeta?.name ?? '')}
            variant="outlined"
            style={{
                backgroundColor: statusMeta?.color,
                color: 'white',
            }}
            size="small"
        />
    );
};

const MatrixCampaignTableRow: FC<{ row: ICampaignMatrix }> = ({ row }) => (
    <TableRow sx={matrixCampaignRowSx} style={{ height: 33 }}>
        <TableCell align="left" width="50%">
            {row.name}
        </TableCell>
        <TableCell align="left">
            <MatrixCampaignStatusChip status={row.status} />
        </TableCell>
    </TableRow>
);

const resolveCampaignFilterValueNames = (
    campaignFilterValues: any[] | undefined,
    campaignFilterTypeId: any,
    ruleTypeObjects: any[]
): string[] => {
    const ruleType = ruleTypeObjects.find((y: any) => y.id == campaignFilterTypeId);

    return (
        campaignFilterValues?.map((x: any) => {
            const filteredValues = ruleType?.values?.filter((z: any) => z.id == x.value);
            return filteredValues?.map((value: any) => value.name);
        }) ?? []
    );
};

const getCampaignFilterRawValues = (campaignFilterValues: any[]) =>
    campaignFilterValues.map((x: any) => x.value);

const hasTrueRuleSwitchValue = (campaignFilterValues: any[]) =>
    campaignFilterValues.some((x: any) => x.value == RuleSwitchValue.True);

const FileProcessingChip: FC<{ fileName?: string; processingLabel: string }> = ({ fileName, processingLabel }) => {
    const tooltipTitle =
        fileName != undefined && fileName.length > 0 ? `${fileName} : ${processingLabel}` : processingLabel;
    const color = '#2962ff';

    return (
        <Tooltip title={tooltipTitle}>
            <Chip
                label={processingLabel}
                size="small"
                variant="outlined"
                avatar={<CachedOutlined style={{ color: color }} />}
                style={{ marginLeft: '4px', marginRight: '8px', borderColor: color, color: color }}
            />
        </Tooltip>
    );
};

const FileProcessSuccessChip: FC<{
    id: any;
    fileName?: string;
    downloadFileLabel: string;
    onDownload: (id: any) => void;
}> = ({ id, fileName, downloadFileLabel, onDownload }) => {
    let chipTitle = '';
    let tooltipTitle = '';

    if (fileName != undefined && fileName.length > 0) {
        chipTitle = fileName.length <= 15 ? fileName : fileName.slice(0, 15) + '...';
        tooltipTitle = fileName;
    } else {
        chipTitle = downloadFileLabel;
        tooltipTitle = downloadFileLabel;
    }

    return (
        <Tooltip title={tooltipTitle}>
            <Chip
                label={chipTitle}
                size="small"
                variant="outlined"
                avatar={<img src={excelLogo} />}
                style={{ marginLeft: '4px', marginRight: '8px' }}
                onClick={() => onDownload(id)}
                onDelete={() => onDownload(id)}
                deleteIcon={<CloudDownload />}
            />
        </Tooltip>
    );
};

const FileProcessFailedChip: FC<{ fileName?: string; message?: string; failedLabel: string }> = ({
    fileName,
    message,
    failedLabel,
}) => {
    const tooltipTitle = fileName != undefined && fileName.length > 0 ? `${fileName} : ${message}` : failedLabel;
    const color = '#d32f2f';

    return (
        <Tooltip title={tooltipTitle}>
            <Chip
                label={failedLabel}
                size="small"
                variant="outlined"
                avatar={<HealingOutlined style={{ color: color }} />}
                style={{ marginLeft: '4px', marginRight: '8px', borderColor: color, color: color }}
            />
        </Tooltip>
    );
};

const SummaryImageWithOverlay: FC<{ badge: any }> = ({ badge }) => {
    const source = badge?.source?.length > 0 ? badge?.source : sampleTshirt;

    return (
        <div
            style={{
                position: 'relative',
                margin: '8px 0',
                display: 'inline-block',
                overflow: 'hidden',
            }}
        >
            <img
                src={source}
                style={{
                    width: '141px',
                    height: '95px',
                    objectFit: 'scale-down',
                }}
            />
        </div>
    );
};

const SummaryImageButtonList: FC<
    ImageButtonListProps & { languages: any; descriptionTexts: any[] | undefined }
> = ({ item, languages, descriptionTexts }) => {
    const { t } = useTranslation();
    const language = languages?.find((x: any) => x.short_code == item.culture);
    const campaignMessage =
        descriptionTexts?.find((x: any) => x.languageId == language?.id)?.keywordValue ?? '';

    return (
        <>
            <div>
                <h4 style={{ marginBottom: '12px' }}>{language?.name ?? '-'}</h4>

                <div style={{ margin: '8px 0' }}>
                    <h5>{t('campaignManagement.save.step.websiteAppearance.form.campaignMessage.title')}</h5>
                    <Typography variant="subtitle2">{campaignMessage}</Typography>
                </div>

                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={4}
                    useFlexGap
                    flexWrap="wrap"
                >
                    {item.badges?.map((badge: any, index: number) => (
                        <Card
                            key={index}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '141px',
                            }}
                        >
                            <Typography variant="caption" style={{ textAlign: 'center' }}>
                                {badge.title}
                            </Typography>
                            <SummaryImageWithOverlay badge={badge} />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                style={{ textAlign: 'center', marginBottom: '4px' }}
                            >
                                {t('campaignManagement.save.step.websiteAppearance.badge.item.badgeName')}
                            </Typography>
                            <Typography
                                variant="caption"
                                style={{ textAlign: 'center', marginBottom: '8px', wordBreak: 'break-word' }}
                            >
                                {badge.badgeName || '-'}
                            </Typography>
                        </Card>
                    ))}
                </Stack>
            </div>

            <Divider sx={{ mt: 2, mr: 2, mb: 2, ml: 0 }} style={{ width: '100%' }} />
        </>
    );
};

export const SCUICampaignSummaryStep = ({ ...props }) => {
    const { t } = useTranslation();

    const Item = styled(Card)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
    }));

    const {
        id: campaignId,
        setStep,
        campaignDetail,
        stepperOnClickReset,
        changeStatus,
    } = StepSummaryServiceComponent({
        ...props,
    });

    const { campaignRule, descriptionTexts } = campaignDetail;

    const { rules, earnings, PageObjects, downloadFileFilter } = StepEarningAndRuleServiceComponent({
        ...props,
    });

    const { badgeButtons, languages } = StepWebsiteAppearenceServiceComponent({
        ...props,
    });

    const { dontMergeList, mergedList, dontApplySameProductList } = StepStackableServiceComponent({
        ...props,
    });

    const { coupons, getStatus, getCharacterSet, getExecutionStatus, downloadCouponFile } = StepCouponServiceComponent({
        ...props,
    });

    const StepProperties = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        return (
            <>
                <Card sx={stepCardSx}>
                    <CardHeader
                        sx={stepCardHeaderSx}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <Tune sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.title')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.Properties)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent sx={stepCardContentSx}>
                        <Typography variant="h3" gutterBottom>
                            {t('campaignManagement.save.step.properties.mainProperties')}
                        </Typography>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.campaignId') + ' :'}
                            </Typography>
                            <Typography variant="body1">{campaignRule?.id}</Typography>
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.campaignName.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">{campaignRule?.title}</Typography>
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.priority') + ' :'}
                            </Typography>
                            <Typography variant="body1">{campaignRule?.processOrder}</Typography>
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.status.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">
                                {t(getCampaignStatusMeta(campaignRule?.status)?.name ?? '')}
                            </Typography>
                        </Box>
                    </CardContent>
                    <Divider sx={stepCardContentDividerSx} />
                    <CardContent sx={stepCardContentSx}>
                        <Typography variant="h3" gutterBottom>
                            {t('campaignManagement.save.step.properties.thirdPartyCampaign')}
                        </Typography>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.tag.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">{campaignRule?.tag}</Typography>
                        </Box>
                    </CardContent>
                    <Divider sx={stepCardContentDividerSx} />
                    <CardContent sx={stepCardContentSx}>
                        <Typography variant="h3" gutterBottom>
                            {t('campaignManagement.save.step.properties.campaignDuration')}
                        </Typography>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.beginDate.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">
                                {campaignRule?.beginDate
                                    ? new Intl.DateTimeFormat(navigator.language).format(
                                          new Date(campaignRule?.beginDate)
                                      )
                                    : ''}
                            </Typography>
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.endDate.title') + ' :'}
                            </Typography>
                            {campaignRule?.endDate
                                ? new Intl.DateTimeFormat(navigator.language).format(new Date(campaignRule?.endDate))
                                : ''}
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.reiteration.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">
                                {t(CampaignRepeatTypes.find((x: any) => x.id == campaignRule?.repeatType)?.name ?? '')}
                            </Typography>
                        </Box>

                        {campaignRule?.repeatType == CampaignRepeatType.Weekly && (
                            <Box sx={stepCardContentBoxSx}>
                                <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                    {t('campaignManagement.save.step.properties.recurrence.title') + ' :'}
                                </Typography>
                                <Typography variant="body1">
                                    {campaignDetail?.repeateDays != undefined &&
                                        CampaignRepeatDays.filter((x: any) =>
                                            campaignDetail?.repeateDays?.includes(x.id)
                                        )
                                            .map((y: any) => t(y.name))
                                            .join(', ')}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.startTime.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">{moment(campaignRule?.beginDate).format('HH:mm')}</Typography>
                        </Box>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.endTime.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">{moment(campaignRule?.endDate).format('HH:mm')}</Typography>
                        </Box>
                    </CardContent>
                    <Divider sx={stepCardContentDividerSx} />
                    <CardContent sx={stepCardContentSx}>
                        <Typography variant="h3" gutterBottom>
                            {t('campaignManagement.save.step.properties.backOfficeUsers')}
                        </Typography>
                        <Box sx={stepCardContentBoxSx}>
                            <Typography variant="subtitle2" sx={stepCardContentBoxTitleSx}>
                                {t('campaignManagement.save.step.properties.assignee.title') + ' :'}
                            </Typography>
                            <Typography variant="body1">{campaignRule?.assignee}</Typography>
                        </Box>
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const StepRulesAndEarnings = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        const RuleConditionItem = (props: any) => {
            const { filter } = props;

            const ValueLabel = ({ ...props }) => {
                const { data } = props;

                let component: any = <></>;

                let values: string[] = resolveCampaignFilterValueNames(
                    data?.campaignFilterValues,
                    data?.campaignFilterTypeId,
                    PageObjects.RuleCondition.ruleTypeObjects
                );

                switch (data.campaignFilterTypeId) {
                    case RuleType.CustomerType:
                    case RuleType.Platform:
                    case RuleType.DeliveryMethod:
                    case RuleType.ShippingCompany:
                    case RuleType.PaymentMethod:
                    case RuleType.PaymentCollector:
                    case RuleType.CreditCardType:
                        component = (
                            <Tooltip title={values.join(', ')}>
                                <Typography>{values.join(', ')}</Typography>
                            </Tooltip>
                        );

                        break;
                    case RuleType.UsageLimit:
                    case RuleType.OrderAmount:
                    case RuleType.Quantity:
                        component = <>{getCampaignFilterRawValues(data.campaignFilterValues)}</>;
                        break;
                    case RuleType.AtFirstOrder:
                    case RuleType.OnlyNewCustomers:
                    case RuleType.NewsCustomerMustJoinedAfterStartDate:
                        component = (
                            <Switch checked={hasTrueRuleSwitchValue(data.campaignFilterValues)} />
                        );
                        break;
                    case RuleType.CustomerInformation:
                    case RuleType.Option:
                    case RuleType.Store:
                        switch (data.executionStatus) {
                            case RuleExecutionType.Queue:
                                component = (
                                    <FileProcessingChip
                                        fileName={data.userFileName}
                                        processingLabel={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.processing'
                                        )}
                                    />
                                );
                                break;
                            case RuleExecutionType.Completed:
                                component = (
                                    <FileProcessSuccessChip
                                        id={data.id}
                                        fileName={data.userFileName}
                                        downloadFileLabel={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                                        )}
                                        onDownload={downloadFileFilter}
                                    />
                                );
                                break;
                            case RuleExecutionType.Error:
                                component = (
                                    <FileProcessFailedChip
                                        fileName={data.userFileName}
                                        message={data.message}
                                        failedLabel={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.failed'
                                        )}
                                    />
                                );
                                break;
                        }

                        break;
                }

                return component;
            };

            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                        textAlign: 'center',
                    }}
                    style={{ border: '1px solid #dadada' }}
                >
                    <Stack
                        spacing={1}
                        direction="row"
                        alignItems="center"
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ width: '35%', textAlign: 'left', paddingLeft: '8px' }}>
                            <Typography noWrap style={{ fontWeight: 'bold' }}>
                                {t(
                                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.condition'
                                )}
                            </Typography>
                            <Typography noWrap>
                                {PageObjects.Rule.ruleTypeObjects.find((r: any) => r.id == filter.campaignFilterTypeId)
                                    ?.name ?? ''}
                            </Typography>
                        </div>
                        <div style={{ width: '15%' }}>
                            <Typography noWrap style={{ fontWeight: 'bold' }}>
                                {t(
                                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.operator'
                                )}
                            </Typography>
                            <Typography noWrap>
                                {PageObjects.Rule.conditionObjects.find((c: any) => c.id == filter.conditionId)?.name ??
                                    ''}
                            </Typography>
                        </div>
                        <div style={{ width: '35%' }}>
                            <Typography noWrap style={{ fontWeight: 'bold' }}>
                                {t(
                                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.value'
                                )}
                            </Typography>
                            <Typography noWrap>
                                <ValueLabel data={filter} />
                            </Typography>
                        </div>
                    </Stack>
                </Item>
            );
        };

        const RuleItem: FC<any> = ({ rule }) => {
            const { name, campaignFilters } = rule;

            return (
                <Card
                    style={{
                        border: '1px solid #dadada',
                        padding: '16px',
                        marginBottom: '16px',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" style={{ minWidth: '80px' }}>
                            {t('campaignManagement.save.step.rulesAndEarnings.rule.item.ruleName')}
                        </Typography>
                        <Typography
                            style={{
                                fontWeight: '700',
                                marginLeft: '4px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {name}
                        </Typography>
                    </div>

                    <Divider
                        style={{
                            width: '100%',
                            margin: '16px 0',
                            color: '#dadada',
                        }}
                    />

                    {campaignFilters.length > 0 &&
                        campaignFilters.map((filter: any) => {
                            return <RuleConditionItem filter={filter} key={filter.id} />;
                        })}

                    {campaignFilters.length == 0 && (
                        <Item
                            sx={{
                                my: 1,
                                p: 2,
                            }}
                            style={{ border: '1px solid #dadada', textAlign: 'center' }}
                        >
                            <Typography noWrap>
                                {t('campaignManagement.save.step.rulesAndEarnings.rule.item.noRecordMessage')}
                            </Typography>
                        </Item>
                    )}
                </Card>
            );
        };

        const EarningItem: FC<any> = ({ earning }) => {
            let { name, campaignFilterRuleId } = earning;

            const ruleId = Number(campaignFilterRuleId ?? 0);

            const ruleName = rules.find((r: any) => r.id == ruleId)?.name ?? '';

            return (
                <Item
                    sx={{
                        mb: 1,
                        p: 2,
                    }}
                    style={{ border: '1px solid #dadada' }}
                >
                    <Grid container style={{ alignItems: 'center' }}>
                        <Grid
                            size={{
                                xs: 5,
                                sm: 5,
                                md: 5,
                                lg: 5,
                            }}
                        >
                            <Typography noWrap>
                                <Tooltip title={name}>
                                    {name.length > 35 ? `${name.substring(0, 35)}...` : name}
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid
                            size={{
                                xs: 4,
                                sm: 4,
                                md: 4,
                                lg: 5,
                            }}
                        >
                            <Typography noWrap>
                                <Tooltip title={ruleName}>
                                    {ruleName.length > 35 ? `${ruleName.substring(0, 35)}...` : ruleName}
                                </Tooltip>
                            </Typography>
                        </Grid>
                    </Grid>
                </Item>
            );
        };

        return (
            <>
                <Card sx={stepCardSx}>
                    <CardHeader
                        sx={stepCardHeaderSx}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <LocalAtm sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.rulesAndEarnings.title')}
                        subheader={t('campaignManagement.save.step.rulesAndEarnings.description')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.RulesAndEarnings)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent sx={stepCardContentSx}>
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 6,
                                    sm: 6,
                                    md: 6,
                                    lg: 6,
                                }}
                            >
                                <Paper style={{ marginBottom: '20px' }}>
                                    <Typography variant="h6" gutterBottom style={{ fontSize: '1.8rem' }}>
                                        {t('campaignManagement.save.step.rulesAndEarnings.rule.title')}
                                    </Typography>

                                    {rules.length > 0 &&
                                        rules.map((rule: any, index: number) => (
                                            <RuleItem rule={rule} index={index} key={index} />
                                        ))}
                                </Paper>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    sm: 6,
                                    md: 6,
                                    lg: 6,
                                }}
                            >
                                <Paper style={{ marginBottom: '20px' }}>
                                    <Typography variant="h6" gutterBottom style={{ fontSize: '1.8rem' }}>
                                        {t('campaignManagement.save.step.rulesAndEarnings.earning.title')}
                                    </Typography>

                                    {earnings.length > 0 &&
                                        earnings.map((earning: any, index: number) => {
                                            return <EarningItem key={index} earning={earning} index={index} />;
                                        })}
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const StepWebsiteAppearance = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        return (
            <>
                <Card sx={stepCardSx}>
                    <CardHeader
                        sx={stepCardHeaderSx}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <Web sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.websiteAppearance.title')}
                        subheader={t('campaignManagement.save.step.websiteAppearance.description')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.WebsiteAppearance)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent sx={stepCardContentSx}>
                        <Box>
                            {badgeButtons?.map((item: any, index: number) => (
                                <SummaryImageButtonList
                                    key={index}
                                    item={item}
                                    languages={languages}
                                    descriptionTexts={descriptionTexts}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const StepStackables = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        const DontMerge = () => {
            return (
                <Card style={{ border: '1px solid #ccc' }}>
                    <CardContent style={{ padding: '40px' }}>
                        <Tooltip title={t(MatrixCampaignTypes.DontMerge.title)} placement="bottom-start">
                            <Typography component="h2" sx={stackableItemTitleSx}>
                                <>{t(MatrixCampaignTypes.DontMerge.title)}</>
                            </Typography>
                        </Tooltip>

                        <Tooltip title={t(MatrixCampaignTypes.DontMerge.description)}>
                            <Typography color="text.secondary" gutterBottom sx={stackableItemDescriptionSx}>
                                {t(MatrixCampaignTypes.DontMerge.description)}
                            </Typography>
                        </Tooltip>
                        <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" width="50%">
                                            {t(
                                                'campaignManagement.save.step.stackable.dontMerge.table.header.campaignName'
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t('campaignManagement.save.step.stackable.dontMerge.table.header.status')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>
                            <Box sx={{ height: '280px', overflowY: 'auto' }}>
                                <Table size="small">
                                    <TableBody>
                                        {dontMergeList?.map((row: ICampaignMatrix) => (
                                            <MatrixCampaignTableRow key={row.campaignRuleId} row={row} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </TableContainer>
                    </CardContent>
                </Card>
            );
        };

        const Merged = () => {
            return (
                <Card style={{ border: '1px solid #ccc' }}>
                    <CardContent style={{ padding: '40px' }}>
                        <Tooltip title={t(MatrixCampaignTypes.Merged.title)} placement="bottom-start">
                            <Typography component="h2" sx={stackableItemTitleSx}>
                                <>{t(MatrixCampaignTypes.Merged.title)}</>
                            </Typography>
                        </Tooltip>
                        <Tooltip title={t(MatrixCampaignTypes.Merged.description)}>
                            <Typography color="text.secondary" gutterBottom sx={stackableItemDescriptionSx}>
                                {t(MatrixCampaignTypes.Merged.description)}
                            </Typography>
                        </Tooltip>
                        <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" width="50%">
                                            {t(
                                                'campaignManagement.save.step.stackable.merged.table.header.campaignName'
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t('campaignManagement.save.step.stackable.merged.table.header.status')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>
                            <Box sx={{ height: '280px', overflowY: 'auto' }}>
                                <Table size="small">
                                    <TableBody>
                                        {mergedList?.map((row: ICampaignMatrix) => (
                                            <MatrixCampaignTableRow key={row.campaignRuleId} row={row} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </TableContainer>
                    </CardContent>
                </Card>
            );
        };

        const DontApplySameProducts = () => {
            return (
                <Card style={{ border: '1px solid #ccc' }}>
                    <CardContent style={{ padding: '40px' }}>
                        <Tooltip title={t(MatrixCampaignTypes.DontApplySameProducts.title)} placement="bottom-start">
                            <Typography component="h2" sx={stackableItemTitleSx}>
                                <>{t(MatrixCampaignTypes.DontApplySameProducts.title)}</>
                            </Typography>
                        </Tooltip>
                        <Tooltip title={t(MatrixCampaignTypes.DontApplySameProducts.description)}>
                            <Typography color="text.secondary" gutterBottom sx={stackableItemDescriptionSx}>
                                {t(MatrixCampaignTypes.DontApplySameProducts.description)}
                            </Typography>
                        </Tooltip>
                        <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" width="50%">
                                            {t(
                                                'campaignManagement.save.step.stackable.dontApplyToSameProducts.table.header.campaignName'
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t(
                                                'campaignManagement.save.step.stackable.dontApplyToSameProducts.table.header.status'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>
                            <Box sx={{ height: '280px', overflowY: 'auto' }}>
                                <Table size="small">
                                    <TableBody>
                                        {dontApplySameProductList?.map((row: ICampaignMatrix) => (
                                            <MatrixCampaignTableRow key={row.campaignRuleId} row={row} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </TableContainer>
                    </CardContent>
                </Card>
            );
        };

        return (
            <>
                <Card style={{ display: 'flex', alignItems: 'center' }}>
                    <CardHeader
                        style={{ width: '100%', padding: 0 }}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <Widgets sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.stackable.title')}
                        subheader={t('campaignManagement.save.step.stackable.description')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.Stackables)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent sx={stepCardContentSx}>
                        <Grid container>
                            <Grid
                                size={{
                                    xs: 4,
                                    sm: 4,
                                    md: 4,
                                    lg: 4,
                                }}
                            >
                                <DontMerge />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 4,
                                    sm: 4,
                                    md: 4,
                                    lg: 4,
                                }}
                            >
                                <Merged />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 4,
                                    sm: 4,
                                    md: 4,
                                    lg: 4,
                                }}
                            >
                                <DontApplySameProducts />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const StepPriority = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        return (
            <>
                <Card style={{ display: 'flex', alignItems: 'center' }}>
                    <CardHeader
                        style={{ width: '100%', padding: 0 }}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <SwapVert sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.priority.title')}
                        subheader={t('campaignManagement.save.step.priority.description')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.Priority)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent style={{ padding: '8px 0' }}>
                        <Item sx={stepPriorityHeaderItemSx}>
                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '1%' }}>
                                    <Typography>
                                        {t('campaignManagement.save.step.priority.item.column.order')}
                                    </Typography>
                                </div>
                                <div style={{ width: '5%' }}>
                                    <Typography>{t('campaignManagement.save.step.priority.item.column.id')}</Typography>
                                </div>
                                <div style={{ width: '50%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.priority.item.column.campaignName')}
                                    </Typography>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.priority.item.column.beginDate')}
                                    </Typography>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.priority.item.column.endDate')}
                                    </Typography>
                                </div>
                            </Stack>
                        </Item>
                        {campaignDetail?.priorities?.map((p: any, index: number) => {
                                const { id, title, beginDate, endDate, status } = p;
                                const statusMeta = getCampaignStatusMeta(status);

                                const itemSx = campaignId == id ? stepPriorityCurrentItemSx : stepPriorityItemSx;
                                return (
                                    <Item sx={itemSx} key={index}>
                                        <Stack
                                            spacing={2}
                                            direction="row"
                                            alignItems="center"
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-around',
                                                width: '100%',
                                            }}
                                        >
                                            <div style={{ width: '1%' }}>
                                                <Typography style={{ fontWeight: 'bold' }}>{index + 1}</Typography>
                                            </div>
                                            <div style={{ width: '5%' }}>
                                                <Typography style={{ fontWeight: 'bold' }}>{id}</Typography>
                                            </div>
                                            <div style={{ width: '50%' }}>
                                                <Typography
                                                    align="left"
                                                    style={{
                                                        fontWeight: 'bold',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={t(statusMeta?.name ?? '')}
                                                        style={{ fontSize: '16px!important' }}
                                                    >
                                                        <Circle
                                                            style={{
                                                                height: 16,
                                                                width: 16,
                                                                fontSize: '16px',
                                                                color: statusMeta?.color,
                                                                marginRight: '8px',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={title}>
                                                        {title.length > 75 ? `${title.substring(0, 75)}...` : title}
                                                    </Tooltip>
                                                </Typography>
                                            </div>
                                            <div style={{ width: '15%' }}>
                                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                                    {new Intl.DateTimeFormat(navigator.language).format(
                                                        new Date(beginDate)
                                                    )}
                                                </Typography>
                                            </div>
                                            <div style={{ width: '15%' }}>
                                                <Typography align="left" style={{ fontWeight: 'bold' }}>
                                                    {new Intl.DateTimeFormat(navigator.language).format(
                                                        new Date(endDate)
                                                    )}
                                                </Typography>
                                            </div>
                                        </Stack>
                                    </Item>
                                );
                            })}
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const StepCoupons = () => {
        const [visible, setVisible] = useState(true);

        const onChange = () => {
            setVisible(!visible);
        };

        const EmptyItem = () => {
            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                    }}
                    style={{ textAlign: 'center' }}
                >
                    <img src={emptyCampaign} style={{ maxHeight: '250px' }} />
                    <Typography style={{ margin: '20px' }}>
                        {t('campaignManagement.save.step.coupon.noRecordMessage')}
                    </Typography>
                </Item>
            );
        };

        //List Items

        const GenericCouponItem: FC<ICouponItem> = memo(({ item }) => {
            let couponName = item.couponName;
            let status: any = getStatus(item.status);
            let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);

            let couponType = t(
                CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Generic)?.name ?? ''
            );
            let couponCode = item.couponCode;
            let cultureCode = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryCode || 'tr-TR';

            let totalCouponCount = new Intl.NumberFormat(cultureCode.toString()).format(item.totalCouponCount ?? 0);
            let loadedCount = new Intl.NumberFormat(cultureCode.toString()).format(item.loadedCouponCount ?? 0);
            let perCustomerCouponCount = item.perCustomerCouponCount;

            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                    }}
                    variant="outlined"
                    style={{ border: '1px solid #ebebeb' }}
                >
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px' }}>
                                        {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                        <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                    </span>
                                    <span style={{ padding: '0 8px' }}>
                                        <Chip label={t(status.text)} size="small" color={status.color} />
                                    </span>
                                </div>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: '350px',
                                    }}
                                >
                                    <Box sx={{ width: '100%', textAlign: 'right' }}>
                                        <Tooltip title={executionStatus.message}>
                                            <Chip
                                                label={t(executionStatus.text)}
                                                size="small"
                                                color={executionStatus.color}
                                                style={{ color: executionStatus.textColor }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider style={{ maxWidth: '100%', margin: '4px 0' }} />
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '20%' }}>
                                    <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                    <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.couponCode')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {couponCode}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {totalCouponCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {loadedCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.perCustomerCouponCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {perCustomerCouponCount}
                                    </Typography>
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                </Item>
            );
        });

        const UniqueCouponItem: FC<ICouponItem> = memo(({ item }) => {
            let couponName = item.couponName;
            let status: any = getStatus(item.status);
            let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);

            let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Unique)?.name ?? '');
            let characterSet = getCharacterSet(item.characterSet);
            let cultureCode = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryCode || 'tr-TR';

            let totalCouponCount = new Intl.NumberFormat(cultureCode.toString()).format(item.totalCouponCount ?? 0);

            let loadedCount = new Intl.NumberFormat(cultureCode.toString()).format(item.loadedCouponCount ?? 0);

            let perCustomerCouponCount = item.perCustomerCouponCount;

            let downloadButtonLabel = '';
            let downloadButtonTooltipLabel = '';
            if (item.userFileName != undefined && item.userFileName.length > 0) {
                downloadButtonLabel =
                    item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
                downloadButtonTooltipLabel = item.userFileName;
            } else {
                downloadButtonLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
                downloadButtonTooltipLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
            }

            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                    }}
                    variant="outlined"
                    style={{ border: '1px solid #ebebeb' }}
                >
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px' }}>
                                        {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                        <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                    </span>
                                    <span style={{ padding: '0 8px' }}>
                                        <Chip label={t(status.text)} size="small" color={status.color} />
                                    </span>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: '350px',
                                    }}
                                >
                                    <Box sx={{ width: '100%', textAlign: 'right' }}>
                                        <Tooltip title={executionStatus.message}>
                                            <Chip
                                                label={t(executionStatus.text)}
                                                size="small"
                                                color={executionStatus.color}
                                                style={{ color: executionStatus.textColor }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '4px 0' }} />
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '20%' }}>
                                    <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                    <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.characterSet')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {characterSet}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {totalCouponCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {loadedCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.perCustomerCouponCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {perCustomerCouponCount}
                                    </Typography>
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                    {item.executionStatus == CouponExecutionType.Completed && (
                        <>
                            <Divider style={{ width: '100%', margin: '4px 0' }} />
                            <Grid container>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <Tooltip title={downloadButtonTooltipLabel}>
                                        <Chip
                                            label={downloadButtonLabel}
                                            size="small"
                                            variant="outlined"
                                            avatar={<img src={excelLogo} />}
                                            style={{ marginRight: '8px' }}
                                            onClick={() => downloadCouponFile(item.id)}
                                            onDelete={() => downloadCouponFile(item.id)}
                                            deleteIcon={<CloudDownload />}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Item>
            );
        });

        const PersonalCouponItem: FC<ICouponItem> = memo(({ item }) => {
            let couponName = item.couponName;
            let status: any = getStatus(item.status);
            let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);

            let couponType = t(
                CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Personal)?.name ?? ''
            );
            let characterSet = getCharacterSet(item.characterSet);
            let cultureCode = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryCode || 'tr-TR';

            let totalCouponCount = new Intl.NumberFormat(cultureCode.toString()).format(item.totalCouponCount ?? 0);
            let loadedCount = new Intl.NumberFormat(cultureCode.toString()).format(item.loadedCouponCount ?? 0);
            let perCustomerCouponCount = item.perCustomerCouponCount;

            let downloadButtonLabel = '';
            let downloadButtonTooltipLabel = '';
            if (item.userFileName != undefined && item.userFileName.length > 0) {
                downloadButtonLabel =
                    item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
                downloadButtonTooltipLabel = item.userFileName;
            } else {
                downloadButtonLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
                downloadButtonTooltipLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
            }

            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                    }}
                    variant="outlined"
                    style={{ border: '1px solid #ebebeb' }}
                >
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px' }}>
                                        {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                        <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                    </span>
                                    <span style={{ padding: '0 8px' }}>
                                        <Chip label={t(status.text)} size="small" color={status.color} />
                                    </span>
                                </div>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: '350px',
                                    }}
                                >
                                    <Box sx={{ width: '100%', textAlign: 'right' }}>
                                        <Tooltip title={executionStatus.message}>
                                            <Chip
                                                label={t(executionStatus.text)}
                                                size="small"
                                                color={executionStatus.color}
                                                style={{ color: executionStatus.textColor }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '4px 0' }} />
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '20%' }}>
                                    <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                    <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.characterSet')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {characterSet}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {totalCouponCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {loadedCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.perCustomerCouponCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {perCustomerCouponCount}
                                    </Typography>
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                    {item.executionStatus == CouponExecutionType.Completed && (
                        <>
                            <Divider style={{ width: '100%', margin: '4px 0' }} />
                            <Grid container>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <Tooltip title={downloadButtonTooltipLabel}>
                                        <Chip
                                            label={downloadButtonLabel}
                                            size="small"
                                            variant="outlined"
                                            avatar={<img src={excelLogo} />}
                                            style={{ marginRight: '8px' }}
                                            onClick={() => downloadCouponFile(item.id)}
                                            onDelete={() => downloadCouponFile(item.id)}
                                            deleteIcon={<CloudDownload />}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Item>
            );
        });

        const ExcelCouponItem: FC<ICouponItem> = memo(({ item }) => {
            let couponName = item.couponName;
            let status: any = getStatus(item.status);
            let executionStatus: any = getExecutionStatus(item.executionStatus, item.message);

            let couponType = t(CouponGenerationTypes.find((x: any) => x.id == CouponGenerationType.Excel)?.name ?? '');
            let characterSet = getCharacterSet(item.characterSet);
            let cultureCode = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryCode || 'tr-TR';

            let totalCouponCount = new Intl.NumberFormat(cultureCode.toString()).format(item.totalCouponCount ?? 0);
            let loadedCount = new Intl.NumberFormat(cultureCode.toString()).format(item.loadedCouponCount ?? 0);
            let perCustomerCouponCount = item.perCustomerCouponCount;

            let downloadButtonLabel = '';
            let downloadButtonTooltipLabel = '';
            if (item.userFileName != undefined && item.userFileName.length > 0) {
                downloadButtonLabel =
                    item.userFileName.length <= 15 ? item.userFileName : item.userFileName.slice(0, 15) + '...';
                downloadButtonTooltipLabel = item.userFileName;
            } else {
                downloadButtonLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
                downloadButtonTooltipLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );
            }

            return (
                <Item
                    sx={{
                        my: 1,
                        p: 2,
                    }}
                    variant="outlined"
                    style={{ border: '1px solid #ebebeb' }}
                >
                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px' }}>
                                        {t('campaignManagement.save.step.coupon.item.couponName') + ' :'}
                                        <span style={{ fontWeight: '800', margin: '0 5px' }}>{couponName}</span>
                                    </span>
                                    <span style={{ padding: '0 8px' }}>
                                        <Chip label={t(status.text)} size="small" color={status.color} />
                                    </span>
                                </div>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: '350px',
                                    }}
                                >
                                    <Box sx={{ width: '100%', textAlign: 'right' }}>
                                        <Tooltip title={executionStatus.message}>
                                            <Chip
                                                label={t(executionStatus.text)}
                                                size="small"
                                                color={executionStatus.color}
                                                style={{ color: executionStatus.textColor }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '4px 0' }} />
                    <Grid>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '20%' }}>
                                    <Typography>{t('campaignManagement.save.step.coupon.item.couponType')}</Typography>
                                    <Typography style={{ fontWeight: 'bold' }}>{couponType}</Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.characterSet')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {characterSet}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.totalUsageCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {totalCouponCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.loadedCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {loadedCount}
                                    </Typography>
                                </div>
                                <div style={{ width: '20%' }}>
                                    <Typography align="left">
                                        {t('campaignManagement.save.step.coupon.item.perCustomerCouponCount')}
                                    </Typography>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {perCustomerCouponCount}
                                    </Typography>
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                    {item.executionStatus == CouponExecutionType.Completed && (
                        <>
                            <Divider style={{ width: '100%', margin: '4px 0' }} />
                            <Grid container>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <Tooltip title={downloadButtonTooltipLabel}>
                                        <Chip
                                            label={downloadButtonLabel}
                                            size="small"
                                            variant="outlined"
                                            avatar={<img src={excelLogo} />}
                                            style={{ marginRight: '8px' }}
                                            onClick={() => downloadCouponFile(item.id)}
                                            onDelete={() => downloadCouponFile(item.id)}
                                            deleteIcon={<CloudDownload />}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Item>
            );
        });
        //List

        const CouponList = memo(() => {
            return (
                <div style={{ margin: '15px 0' }}>
                    <Stack spacing={2}>
                        {coupons &&
                            coupons.map((coupon: any) => {
                                switch (coupon.generationType) {
                                    case CouponGenerationType.Unique:
                                        return <UniqueCouponItem item={coupon} />;
                                    case CouponGenerationType.Generic:
                                        return <GenericCouponItem item={coupon} />;
                                    case CouponGenerationType.Personal:
                                        return <PersonalCouponItem item={coupon} />;
                                    case CouponGenerationType.Excel:
                                        return <ExcelCouponItem item={coupon} />;
                                }
                            })}

                        {coupons && coupons.length == 0 && <EmptyItem />}
                    </Stack>
                </div>
            );
        });

        return (
            <>
                <Card style={{ display: 'flex', alignItems: 'center' }}>
                    <CardHeader
                        style={{ width: '100%', padding: 0 }}
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <ConfirmationNumber sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.coupon.title')}
                        subheader={t('campaignManagement.save.step.coupon.description')}
                    />
                    <SCUIButton
                        text={t('campaignManagement.save.step.summary.action.editButton')}
                        type="ghost"
                        sizeType="default"
                        size={'s'}
                        buttonType="button"
                        onClick={() => setStep(CampaignStep.Coupon)}
                    />
                    <ExpandMoreComponent expand={visible} onClick={onChange}>
                        <ExpandMore />
                    </ExpandMoreComponent>
                </Card>
                <Collapse in={visible} timeout="auto" unmountOnExit>
                    <CardContent sx={stepCardContentSx}>
                        <CouponList />
                    </CardContent>
                </Collapse>
            </>
        );
    };

    const TemplateActions = () => {
        return (
            <>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.passiveButton')}
                        buttonType="secondary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={
                            <span>
                                {parse(
                                    DOMPurify.sanitize(t('campaignManagement.save.step.summary.dialog.passiveMessage'))
                                )}
                            </span>
                        }
                        onConfirm={() => changeStatus(CampaignStatus.Passive)}
                    />
                </Item>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.testButton')}
                        buttonType="primary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={t('campaignManagement.save.step.summary.dialog.testMessage')}
                        onConfirm={() => changeStatus(CampaignStatus.Test)}
                    />
                </Item>
            </>
        );
    };

    const TestActions = () => {
        return (
            <>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.passiveButton')}
                        buttonType="secondary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={
                            <span>
                                {parse(
                                    DOMPurify.sanitize(t('campaignManagement.save.step.summary.dialog.passiveMessage'))
                                )}
                            </span>
                        }
                        onConfirm={() => changeStatus(CampaignStatus.Passive)}
                    />
                </Item>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.productionButton')}
                        buttonType="primary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={t('campaignManagement.save.step.summary.dialog.productionMessage')}
                        onConfirm={() => changeStatus(CampaignStatus.Active)}
                    />
                </Item>
            </>
        );
    };

    const ActiveActions = () => {
        return (
            <>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.passiveButton')}
                        buttonType="secondary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={
                            <span>
                                {parse(
                                    DOMPurify.sanitize(t('campaignManagement.save.step.summary.dialog.passiveMessage'))
                                )}
                            </span>
                        }
                        onConfirm={() => changeStatus(CampaignStatus.Passive)}
                    />
                </Item>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.pausedButton')}
                        buttonType="primary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={t('campaignManagement.save.step.summary.dialog.pausedMessage')}
                        onConfirm={() => changeStatus(CampaignStatus.Paused)}
                    />
                </Item>
            </>
        );
    };

    const PausedActions = () => {
        return (
            <>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.passiveButton')}
                        buttonType="secondary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={
                            <span>
                                {parse(
                                    DOMPurify.sanitize(t('campaignManagement.save.step.summary.dialog.passiveMessage'))
                                )}
                            </span>
                        }
                        onConfirm={() => changeStatus(CampaignStatus.Passive)}
                    />
                </Item>
                <Item>
                    <ButtonWithConfirmation
                        {...props}
                        buttonText={t('campaignManagement.save.step.summary.action.testButton')}
                        buttonType="primary"
                        confirmationTitle={t('campaignManagement.save.step.summary.dialog.title')}
                        confirmationMessage={t('campaignManagement.save.step.summary.dialog.testMessage')}
                        onConfirm={() => changeStatus(CampaignStatus.Test)}
                    />
                </Item>
            </>
        );
    };

    const ActionsComponent = () => {
        let component: any = <></>;

        if (campaignRule) {
            switch (campaignRule.status) {
                case CampaignStatus.Template:
                    component = <TemplateActions />;
                    break;
                case CampaignStatus.Test:
                    component = <TestActions />;
                    break;
                case CampaignStatus.Active:
                    component = <ActiveActions />;
                    break;
                case CampaignStatus.Paused:
                    component = <PausedActions />;
                    break;
            }
        }

        return (
            <>
                <Item>
                    <SCUIButton
                        text={t('campaignManagement.save.step.action.backButton')}
                        type="secondary"
                        sizeType="icon"
                        size={'s'}
                        buttonType="button"
                        onClick={stepperOnClickReset}
                    />
                </Item>
                {component}
            </>
        );
    };

    return (
        <Card className="step-form-card">
            <form
                id="StepSummary"
                name="StepSummary"
                noValidate={false}
                //   onSubmit={stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <Task sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.summary.title')}
                    action={<SCUILogs {...props} />}
                />

                <CardContent className="step-form-card-content">
                    <StepProperties {...props} />
                    <Divider sx={dividerSx} />
                    <StepRulesAndEarnings {...props} />
                    <Divider sx={dividerSx} />
                    <StepWebsiteAppearance {...props} />
                    <Divider sx={dividerSx} />
                    <StepPriority {...props} />
                    <Divider sx={dividerSx} />
                    <StepStackables {...props} />
                    <Divider sx={dividerSx} />
                    <StepCoupons {...props} />
                    <Divider sx={dividerSx} />
                </CardContent>

                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <ActionsComponent />
                    </Stack>
                </CardActions>
            </form>
        </Card>
    );
};
